import { SHEET_DIAGNOSTIC_ROLE_LABELS, SheetType } from '../models/sheet-types';
import { ConversionPhaseEnum } from '../../types/commons';

/**
 * Names the page or sheet a diagnostic code refers to, in the same vocabulary
 * the page's own load-failure codes use (`MBRS-<scope>-<class>[-<conversion>]`,
 * see `formatLoadDiagnosticCode`).
 *
 * Used by the pre-navigation session gate: when it catches a dead session, the
 * AUTH code names the page the user was LEAVING, because the destination never
 * loads. Decision table: `docs/development/filing-load-diagnostics.md`
 * *AUTH codes from the pre-navigation gate*.
 */
export interface DiagnosticScope {
  /** Scope token placed between `MBRS-` and the failure class, e.g. `XBRL_FILES` or `S7`. */
  scope: string;
  /** Conversion the page/sheet belongs to, or null. */
  conversionId: string | null;
}

/** Pages with a fixed scope token — the token each page already uses for its own load failures. */
const PAGE_SCOPES_BY_PATH: Readonly<Record<string, string>> = {
  '/mbrs/dashboard': 'DASHBOARD_SUMMARY',
  '/mbrs/xbrl-files': 'XBRL_FILES',
  '/mbrs/profile': 'PROFILE',
  '/mbrs/user-management': 'USER_MGMT_LISTING',
};

/** The two phase-2 filing routes, which are always one specific sheet. */
const PHASE_TWO_FILING_SCOPES_BY_PATH: Readonly<Record<string, string>> = {
  '/mbrs/filing/role1': 'S1',
  '/mbrs/filing/role2': 'S2',
};

const FILING_PATH = '/mbrs/filing';

/** `S<role label>` for a known sheet, bare `S` when no sheet number can be matched. */
export function filingSheetScope(sheet: string | null | undefined): string {
  if (sheet != null && Object.prototype.hasOwnProperty.call(SHEET_DIAGNOSTIC_ROLE_LABELS, sheet))
    return `S${SHEET_DIAGNOSTIC_ROLE_LABELS[sheet as SheetType]}`;
  return 'S';
}

/**
 * Key under which a listing row hands the filing page its conversion phase via
 * router navigation `state` (`<a [routerLink] [state]="filingEntryState(row.phase)">`).
 *
 * Why it exists: entering from XBRL Files / the dashboard puts no `?sheet=` in
 * the URL, and the phase otherwise arrives from the very request a stalled load
 * is waiting on — so a stall used to print a bare `S`. The row already knows its
 * phase, and the landing sheet follows from it (Phase 1 → Filing Prerequisites,
 * Phase 2 → Filing Information). `history.state` survives a browser refresh.
 */
export const FILING_ENTRY_PHASE_STATE_KEY = 'filingEntryPhase';

/** Navigation `state` for a listing row's filing link; empty when the row has no phase. */
export function filingEntryState(phase: ConversionPhaseEnum | null | undefined): Record<string, number> {
  return phase === ConversionPhaseEnum.Phase1 || phase === ConversionPhaseEnum.Phase2
    ? { [FILING_ENTRY_PHASE_STATE_KEY]: phase }
    : {};
}

/**
 * `S0` / `S1` for the sheet an entry from a listing row lands on, or `null` when
 * `navigationState` carries no usable phase hint. Only a fallback for a landing
 * sheet that is not settled yet — a `?sheet=` or the settled sheet always wins.
 */
export function scopeFromFilingEntryState(navigationState: unknown): string | null {
  const phase = (navigationState as Record<string, unknown> | null | undefined)?.[FILING_ENTRY_PHASE_STATE_KEY];
  if (phase === ConversionPhaseEnum.Phase1) return filingSheetScope('filing-prerequisites');
  if (phase === ConversionPhaseEnum.Phase2) return filingSheetScope('filing-info');
  return null;
}

/** The scope of the page at `url` (the page being LEFT), or null when no page token applies. */
export function resolveDiagnosticScopeFromUrl(url: string): DiagnosticScope | null {
  let parsed: URL;
  try {
    parsed = new URL(url, 'http://localhost');
  }
  catch {
    return null;
  }

  const path = parsed.pathname;
  const pageScope = PAGE_SCOPES_BY_PATH[path];
  if (pageScope !== undefined)
    return { scope: pageScope, conversionId: null };

  const conversionId = parsed.searchParams.get('id');
  if (path === FILING_PATH)
    return { scope: filingSheetScope(parsed.searchParams.get('sheet')), conversionId };

  const phaseTwoScope = PHASE_TWO_FILING_SCOPES_BY_PATH[path];
  if (phaseTwoScope !== undefined)
    return { scope: phaseTwoScope, conversionId };

  return null;
}
